import { useCallback, useEffect, useRef, useState } from "react";
import { api, errText, media } from "../../lib/api";
import { ShoppingBag, Check, X } from "lucide-react";

/** Ops moderation queue for florist-authored bouquet addons. */
export default function AddonsModerationPage() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState("");
  const currentStatus = useRef(status);
  currentStatus.current = status;
  const load = useCallback((signal) => api.get("/admin/addons", { params: { status }, signal })
    .then((r) => { if (!signal?.aborted && currentStatus.current === status) setRows(r.data); })
    .catch((e) => { if (!signal?.aborted && currentStatus.current === status) setMsg(errText(e)); }), [status]);
  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const decide = async (a, decision) => {
    const notes = decision === "reject" ? window.prompt("Raison du refus (visible par le fleuriste) :") || "" : "";
    try {
      await api.post(`/admin/addons/${a.id}/moderate`, { decision, notes });
      setMsg(decision === "approve" ? "Approuvé." : "Refusé.");
      load();
    } catch (e) { setMsg(errText(e)); }
  };

  return (
    <div className="space-y-4" data-testid="admin-addons-page">
      <header>
        <h1 className="font-display text-3xl text-ink flex items-center gap-2">
          <ShoppingBag size={22} className="text-brass" /> Moderation des ajouts
        </h1>
        <p className="text-sm text-muted mt-1 max-w-3xl">
          Chaque ajout créé ou modifié par un fleuriste passe ici avant d'être visible sur la boutique.
        </p>
      </header>

      <div className="flex gap-2">
        {[["pending", "En attente"], ["approved", "Approuvés"], ["rejected", "Refusés"]].map(([v, l]) => (
          <button key={v} data-testid={`addon-filter-${v}`}
                  className={`btn-ghost text-xs ${status === v ? "!bg-ink !text-plaster" : ""}`}
                  onClick={() => setStatus(v)}>{l}</button>
        ))}
      </div>

      {msg ? <p role="status" className="text-xs text-muted" data-testid="addon-moderation-message">{msg}</p> : null}

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {rows.map((a) => (
          <div key={a.id} className="card p-4 space-y-2" data-testid={`admin-addon-${a.id}`}>
            <div className="flex items-start gap-3">
              {a.image_url ? <img src={media(a.image_url)} alt="" className="w-20 h-20 object-cover border border-brass-light" />
                           : <div className="w-20 h-20 bg-plaster-deep border border-brass-light" />}
              <div className="min-w-0 flex-1">
                <p className="text-ink font-medium">{(a.name?.fr) || a.name?.en || "Sans nom"}</p>
                <p className="text-xs text-muted">{a.shop_name || "—"} · {a.price_mad} MAD</p>
                <p className="text-xs text-muted mt-1">
                  Applies : <b>{a.applies_to}</b>{a.applies_to === "selection" ? ` (${(a.product_ids || []).length} bouquet(s))` : ""}
                </p>
                {a.moderation_notes ? <p className="text-xs text-danger mt-1">{a.moderation_notes}</p> : null}
              </div>
            </div>
            {status === "pending" ? (
              <div className="flex gap-2">
                <button className="btn-primary text-xs" data-testid={`addon-approve-${a.id}`}
                        onClick={() => decide(a, "approve")}>
                  <Check size={12} className="inline -mt-0.5" /> Approuver
                </button>
                <button className="btn-ghost text-xs text-danger" data-testid={`addon-reject-${a.id}`}
                        onClick={() => decide(a, "reject")}>
                  <X size={12} className="inline -mt-0.5" /> Refuser
                </button>
              </div>
            ) : null}
          </div>
        ))}
        {!rows.length ? <p className="text-sm text-muted col-span-full" data-testid="admin-addons-empty">Aucun ajout dans cet état.</p> : null}
      </div>
    </div>
  );
}
